<template>
  <div
    class="segmented"
    :class="columns ? 'segmented--grid' : undefined"
    :style="columns ? { gridTemplateColumns: `repeat(${columns}, 1fr)` } : undefined"
    role="radiogroup"
  >
    <button
      v-for="option in options"
      :key="option.value"
      type="button"
      class="segmented-item"
      :class="option.tone ? `tone-${option.tone}` : undefined"
      role="radio"
      :aria-checked="option.value === modelValue"
      :tabindex="tabIndexFor(option)"
      @click="$emit('update:modelValue', option.value)"
      @keydown="onKeydown"
    >
      <slot name="option" :option="option" :selected="option.value === modelValue">
        <component :is="option.icon" v-if="option.icon" class="w-4 h-4" aria-hidden="true" />
        {{ option.label }}
      </slot>
    </button>
  </div>
</template>

<script setup lang="ts">
import type { Component } from "vue"

export interface SegmentedOption {
  value: string
  label: string
  icon?: Component
  /** Active-state tint; default is the accent. */
  tone?: "success" | "danger"
}

/**
 * Single-select button group (wraps the global .segmented vocabulary) — the
 * "pick one" form control behind expense/income toggles, frequency pickers,
 * account-type grids. v-model holds the selected value. Flows as a flex row
 * by default; pass `columns` for a fixed grid (wizard option pickers).
 * Arrow keys move the selection (roving tabindex radiogroup). Pass an
 * aria-label for the group — attributes fall through to the root element.
 */
const props = defineProps<{
  modelValue: string
  options: SegmentedOption[]
  /** Lay options out in a fixed-column grid instead of a flex row. */
  columns?: number
}>()

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void
}>()

// Roving tabindex: the selected option is tabbable; with no selection yet,
// the first option takes the tab stop.
function tabIndexFor(option: SegmentedOption) {
  const hasSelection = props.options.some((o) => o.value === props.modelValue)
  const anchor = hasSelection ? props.modelValue : props.options[0]?.value
  return option.value === anchor ? 0 : -1
}

function onKeydown(event: KeyboardEvent) {
  const delta = ["ArrowRight", "ArrowDown"].includes(event.key)
    ? 1
    : ["ArrowLeft", "ArrowUp"].includes(event.key)
      ? -1
      : 0
  if (!delta || props.options.length === 0) return
  event.preventDefault()
  const current = props.options.findIndex((o) => o.value === props.modelValue)
  const next = props.options[(current + delta + props.options.length) % props.options.length]
  emit("update:modelValue", next.value)
  // Move focus along with the selection so arrowing keeps working.
  const buttons = (event.currentTarget as HTMLElement).parentElement?.querySelectorAll<HTMLButtonElement>(
    ".segmented-item",
  )
  const nextIndex = (current + delta + props.options.length) % props.options.length
  buttons?.[nextIndex]?.focus()
}
</script>
